/**
 * Relewise facets — renders the theme's designed filter chrome (quick chips +
 * dropdown panels, active-filter row, slide-out drawer accordion + footer)
 * from a Relewise ProductSearch response, and keeps the selection state in
 * the URL. Used by assets/relewise-listing.js for the search results page and
 * the Relewise category page.
 *
 * WHAT decides WHICH facets exist: the Relewise Shopify app's Search
 * Configuration (window.ShopifyRelewise.searchConfigurations.{search,collection}).
 * Spejder Sport edits facets, headings, order and sortings there after
 * handover — this module renders whatever the configuration returns and
 * never hard-codes a facet key. Presentation (swatch / pill / checkbox /
 * alpha index / default-open) is matched on the facet HEADING through the
 * section's existing display-style settings, exactly like the Shopify
 * filter snippets do.
 *
 * URL contract (shareable, bookmarkable):
 *   ?q=<term>&f.<key>=<value>&f.<key>=<value>…&price=<min>-<max>&sort_by=<id>&page=<n>
 *   <key>   = the facet's key as configured in the app (custom.category_type,
 *             IsAvailable …) — the shop-domain prefix is added/stripped here.
 *   <id>    = the sorting option's id from the configuration (Relevance,
 *             SalesPriceAsc, … or a productData sorting id).
 *
 * Markup comes from <template data-rw-tpl="…"> blocks the section renders
 * (icons, translated strings, exact Figma classes), cloned and filled here —
 * the same approach as the search page's pagination. Every interactive
 * element carries data-rw-action / data-rw-key / data-rw-value; the page
 * script listens for those and re-runs the search without a reload.
 *
 * Request side: the app's apply(builder) adds the configured facets WITHOUT
 * selections (the app's own web components keep selection state). Selections
 * are written onto the BUILT request (applySelections) — post-build, because
 * window.Relewise is whichever SDK loads last (the app embed's copy) and its
 * builder API is not guaranteed.
 */
!(function () {
  'use strict';

  const cfg = window.spejder && window.spejder.relewise;
  const shop = (cfg && cfg.shop) || '';
  const PREFIX = 'f.';

  /* ---------- URL state ---------- */

  /** {selections: {key: [values]}, price: {min, max} | null} */
  function readState(params) {
    const selections = {};
    params.forEach((value, name) => {
      if (name.indexOf(PREFIX) !== 0 || !value) return;
      const key = name.slice(PREFIX.length);
      (selections[key] = selections[key] || []).push(value);
    });
    let price = null;
    const p = params.get('price');
    if (p) {
      const m = p.match(/^(\d*)-(\d*)$/);
      if (m && (m[1] || m[2])) price = { min: m[1] ? parseInt(m[1], 10) : null, max: m[2] ? parseInt(m[2], 10) : null };
    }
    return { selections, price };
  }

  function writeState(params, state) {
    [...params.keys()].forEach((name) => { if (name.indexOf(PREFIX) === 0 || name === 'price') params.delete(name); });
    Object.keys(state.selections).forEach((key) => {
      state.selections[key].forEach((value) => params.append(PREFIX + key, value));
    });
    if (state.price && (state.price.min != null || state.price.max != null)) {
      params.set('price', `${state.price.min != null ? state.price.min : ''}-${state.price.max != null ? state.price.max : ''}`);
    }
    return params;
  }

  function activeCount(state) {
    let n = 0;
    Object.keys(state.selections).forEach((k) => { n += state.selections[k].length; });
    if (state.price) n += 1;
    return n;
  }

  /* ---------- keys ---------- */

  const stripShop = (key) => (shop && key && key.indexOf(shop + '_') === 0 ? key.slice(shop.length + 1) : key);

  /** Configured facets (enabled, in app order) → {key, heading, type}. */
  function configuredFacets(config, locale) {
    if (!config || !Array.isArray(config.facets)) return [];
    return config.facets.filter((f) => f && f.enabled !== false).map((f) => ({
      key: f.dataObjectKey || f.key || '',
      heading: (f.headingsByLocale && f.headingsByLocale[locale]) || f.defaultHeading || '',
      type: f.type,
    }));
  }

  /* ---------- request ---------- */

  /**
   * Writes the URL selections onto the built request's facet items. Matched
   * by facet type + key (shop-prefixed or not — the app prefixes keys that
   * "require shop domain").
   */
  function applySelections(request, state) {
    const items = request && request.facets && request.facets.items;
    if (!items) return request;
    items.forEach((item) => {
      const type = item.$type || '';
      /* Shopify metafield keys are always shop-prefixed in Relewise
         (<shop>_custom.<key>). A facet typed into the app's picker instead of
         chosen from it is saved with "requires shop domain" OFF (app bug,
         reported 2026-09-10 — custom.sizes; again 2026-09-29 —
         specifications.lynlasplacering), so the app sends the bare key and
         Relewise answers with no values. Prefix it here. */
      if (shop && item.key && /^(custom|theme|shopify|specifications)\./.test(item.key)) item.key = `${shop}_${item.key}`;
      if (type.indexOf('PriceRangeFacet,') !== -1 || type.indexOf('PriceRangeFacet ') !== -1) {
        item.selected = state.price
          ? { lowerBoundInclusive: state.price.min != null ? state.price.min : null, upperBoundInclusive: state.price.max != null ? state.price.max : null }
          : null;
        return;
      }
      const key = stripShop(item.key || '');
      const values = state.selections[key];
      if (!values || !values.length) { item.selected = null; return; }
      if (type.indexOf('BooleanValueFacet') !== -1) item.selected = values.map((v) => v === 'true' || v === '1');
      else if (type.indexOf('DoubleValueFacet') !== -1) item.selected = values.map(Number).filter((n) => !isNaN(n));
      else item.selected = values.slice();
    });
    return request;
  }

  /* ---------- sizes: one facet, "Tøj/L" and "Sko/42" values ---------- */

  /* The Spejder Sport app writes custom.sizes with a group prefix ("Tøj/L",
     "Sko/42", "Udstyr/S/M"): ONE facet in the app called "Størrelse", ONE
     pill, and multi-select across the groups works because Relewise sees one
     facet (two keys were two facets — picking a shoe size emptied the
     clothing list, Magnus 2026-09-10). The theme splits the values on the
     FIRST "/" into one section per group; the URL carries the prefixed
     value. The group names and their order come from the merchant's "Size
     filter groups" in the Spejder Sport app (shop metafield
     spejder_automation.size_group_settings, emitted by
     relewise-facet-templates.liquid as data-rw-size-groups) — a prefix that
     is not a known group is not split, so legacy "S/M" stays one value in a
     section without heading. Explicitly configured "Tøjstørrelse"/
     "Skostørrelse" facets (the section's size_group_filters) still cluster
     the same way. */
  const SIZES_KEY = 'custom.sizes';
  const sizeGroups = (root) => { const list = settingList(root, 'data-rw-size-groups'); return list.length ? list : ['Tøj', 'Sko']; };
  const sizeHeading = (root) => (root.getAttribute('data-rw-size-heading') || 'Størrelse');
  const clusterLabels = (root) => settingList(root, 'data-rw-size-cluster');
  const isSizeFacet = (root, facet) => !!facet.heading && facet.heading.toLowerCase() === sizeHeading(root).toLowerCase();
  const inSizeCluster = (root, facet) => labelIn(facet.heading, clusterLabels(root));
  const subLabel = (heading) => {
    const stripped = heading.replace(/størrelse/i, '').trim();
    return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : heading;
  };
  const splitPrefixed = (root, value) => {
    const i = value.indexOf('/');
    if (i > 0) {
      const g = value.slice(0, i);
      const known = sizeGroups(root).find((name) => name.toLowerCase() === g.toLowerCase());
      if (known) return { group: known, label: value.slice(i + 1) };
    }
    return { group: null, label: value };
  };

  const LETTER_ORDER = ['XXXS', '3XS', 'XXS', '2XS', 'XS', 'XS/S', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', 'XXL', '2XL', 'XXXL', '3XL', '4XL', '5XL', 'ONE SIZE', 'ONESIZE', 'OS'];
  /* Shoe sizes the Spejder Sport app writes with their system ("US 8 Herre",
     "UK 6 Dame": Settings → Shoe size systems) follow the EU numbers, by
     system and then by number, so "US 6" comes before "US 10" (a text sort put
     "US 10" first). spejder-size-plan.liquid sorts the same way. */
  const SIZE_SYSTEMS = ['AUS', 'UK', 'US'];
  function sizeSortKey(label) {
    const t = String(label).trim().toUpperCase();
    const li = LETTER_ORDER.indexOf(t);
    if (li !== -1) return [0, li, t];
    const sys = t.match(/^(AUS|UK|US) (?=\d)/);
    const m = t.slice(sys ? sys[0].length : 0).match(/^(\d+(?:[.,]\d+)?)(?:\s+(\d)\/(\d))?/);
    if (m) {
      let n = parseFloat(m[1].replace(',', '.'));
      if (m[2]) n += parseInt(m[2], 10) / parseInt(m[3], 10);
      if (sys) return [2, (SIZE_SYSTEMS.indexOf(sys[1]) + 1) * 100 + n, t];
      return [1, n, t];
    }
    return [3, 0, t];
  }
  /* Available values first in size order, then a selected 0-hit one (the
     only 0-hit values model() keeps) — the Search & Discovery drawer's
     behaviour. */
  function sortSizes(values) {
    return values.slice().sort((a, b) => {
      if (!!a.count !== !!b.count) return a.count ? -1 : 1;
      const ka = sizeSortKey(a.label); const kb = sizeSortKey(b.label);
      if (ka[0] !== kb[0]) return ka[0] - kb[0];
      if (ka[1] !== kb[1]) return ka[1] - kb[1];
      return ka[2] < kb[2] ? -1 : ka[2] > kb[2] ? 1 : 0;
    });
  }

  /* Folds size facets into one virtual facet {kind:'cluster', heading,
     sections:[{heading, key, values}]} at the configured facet's position. */
  function clusterSizes(root, facets) {
    const out = []; let cluster = null;
    const ensure = () => { if (!cluster) { cluster = { key: 'sizes', heading: sizeHeading(root), kind: 'cluster', sections: [] }; out.push(cluster); } return cluster; };
    const section = (heading, key) => {
      const c = ensure();
      let sec = c.sections.find((x) => x.heading === heading && x.key === key);
      if (!sec) { sec = { heading, key, values: [] }; c.sections.push(sec); }
      return sec;
    };
    facets.forEach((facet) => {
      if (facet.kind !== 'list') { out.push(facet); return; }
      if (inSizeCluster(root, facet)) {
        const sec = section(subLabel(facet.heading), facet.key);
        facet.values.forEach((v) => sec.values.push(v));
        return;
      }
      if (isSizeFacet(root, facet)) {
        ensure();
        facet.values.forEach((v) => {
          const p = splitPrefixed(root, v.value);
          section(p.group, facet.key).values.push({ value: v.value, label: p.label, count: v.count, selected: v.selected });
        });
        return;
      }
      out.push(facet);
    });
    if (cluster) {
      cluster.sections.forEach((sec) => { sec.values = sortSizes(sec.values); });
      /* Merchant order first (Tøj, Sko, Udstyr …), then unprefixed values,
         then explicitly configured cluster facets. */
      const names = sizeGroups(root).map((n) => n.toLowerCase());
      const order = (h) => { if (h === null) return names.length; const i = names.indexOf(String(h).toLowerCase()); return i === -1 ? names.length + 1 : i; };
      cluster.sections.sort((a, b) => order(a.heading) - order(b.heading));
      const filled = cluster.sections.filter((sec) => sec.values.length);
      /* No section has a value (no size with hits, none selected): nothing
         to offer, so the Størrelse chip is not rendered — same rule as every
         other facet (see model()). */
      if (!filled.length) return out.filter((f) => f !== cluster);
      cluster.sections = filled;
    }
    return out;
  }

  /** Sections to render for a list or cluster facet: [{heading|null, key, values}].
      A value with its own `key` toggles that key instead (mergeStock). */
  const sectionsOf = (facet) => facet.kind === 'cluster' ? facet.sections : [{ heading: null, key: facet.key, values: facet.values }];
  const activeIn = (facet) => sectionsOf(facet).reduce((n, sec) => n + sec.values.filter((v) => v.selected).length, 0);

  /* ---------- only values the result can reach ---------- */

  /* A value without hits is not listed, not even greyed out, on any facet
     (client card MHDBArN1 + Magnus 2026-10-07): a facet with nothing left
     drops its chip and drawer group, and a selected value stays, with 0, so
     its filter can be undone. Search & Discovery's lists follow the same rule
     (filter-values-list, spejder-facet-chips, spejder.js dropEmpty). This
     replaces the greyed-out values each listing remembered for the tab
     (2026-09-17), which on /search were shared by every query: one search's
     brands and sizes were listed, greyed out, under the next. */
  const offered = (v) => v.count > 0 || v.selected;

  /* ---------- response → model ---------- */

  /**
   * [{key, heading, kind: 'list'|'price', values: [{value, label, count, selected}],
   *   range: {min, max, selMin, selMax}}] — in configuration order.
   */
  function model(result, facets, state) {
    const items = (result && result.facets && result.facets.items) || [];
    const out = [];
    /* Headings come from the configuration, matched by KEY (price: by type) —
       never by position, since the app skips facet types it does not know. */
    const confFor = (item) => {
      const type = item.$type || '';
      if (type.indexOf('PriceRange') !== -1) return facets.find((f) => f.type === 'SalesPriceRangeFacet' || f.type === 'SalesPriceRangesFacet') || {};
      const key = stripShop(item.key || '');
      return facets.find((f) => f.key === key) || {};
    };
    items.forEach((item) => {
      const type = item.$type || '';
      const conf = confFor(item);
      if (type.indexOf('PriceRangeFacetResult') !== -1) {
        const av = (item.available && item.available.value) || {};
        const sel = item.selected || {};
        if (av.lowerBoundInclusive == null && av.upperBoundInclusive == null) return;
        out.push({
          key: 'price', heading: conf.heading || 'Pris', kind: 'price',
          range: {
            min: Math.floor(av.lowerBoundInclusive || 0),
            max: Math.ceil(av.upperBoundInclusive || 0),
            selMin: sel.lowerBoundInclusive != null ? Math.floor(sel.lowerBoundInclusive) : null,
            selMax: sel.upperBoundInclusive != null ? Math.ceil(sel.upperBoundInclusive) : null,
          },
        });
        return;
      }
      const available = Array.isArray(item.available) ? item.available : [];
      const boolean = type.indexOf('BooleanValueFacet') !== -1;
      const itemKey = stripShop(item.key || conf.key || '');
      const values = available
        .filter((a) => a && a.value != null && a.value !== '')
        /* A Yes/No facet ("Kun på lager") is a single opt-in: only the "Ja"
           value renders — "Nej" would mean "only sold-out products". */
        .filter((a) => !boolean || a.value === true)
        /* The Produkt type facet reads Shopify's product type, which is the
           BC item category (2026-09-28, client card qJ9K5tlj). BC still sends
           the placeholder "new" on items without a category (343 of 3,659 on
           the day of the switch); it is not a product type, so it never
           renders as a value. Gone by itself once BC fills the categories. */
        .filter((a) => !(itemKey === 'ProductType' && String(a.value).toLowerCase() === 'new'))
        .map((a) => ({
          value: String(a.value),
          label: typeof a.value === 'boolean' ? 'Ja' : String(a.value),
          count: a.hits || 0,
          selected: !!a.selected,
        }));
      const key = itemKey;
      const all = values.filter(offered);
      /* A value the shopper has selected that the response does not list back
         (a stale bookmark, a value retired from the catalogue) would otherwise
         render no pill and no "Ryd alle" — a filter with no way out. */
      (state && state.selections[key] ? state.selections[key] : []).forEach((value) => {
        if (!all.some((v) => v.value === value)) all.push({ value, label: value, count: 0, selected: true });
      });
      /* A facet with no value to show — none with hits in this answer, none
         selected — is not rendered: a collection that never carries sizes
         ("Telte") must not show a dead Størrelse chip (client card M4LZd72W,
         2026-09-28), nor a search whose results have no "Antal personer". */
      if (!all.length) return;
      /* Available values alphabetically (the Brand A–Z index relies on it),
         then a selected one without hits; Relewise's own order is by hits.
         Size facets get their own natural order in clusterSizes(). */
      all.sort((a, b) => (!!a.count !== !!b.count ? (a.count ? -1 : 1) : a.label.localeCompare(b.label, 'da', { numeric: true, sensitivity: 'base' })));
      out.push({ key, heading: conf.heading || key, kind: 'list', values: all });
    });
    /* Configured facets Relewise answered nothing at all for keep their chip
       only for a selection the shopper must be able to undo. Otherwise the
       facet does not apply to this listing and is left out (same rule as
       above). */
    facets.forEach((conf) => {
      if (!conf.key || conf.type === 'SalesPriceRangeFacet' || conf.type === 'SalesPriceRangesFacet') return;
      if (out.some((f) => f.key === conf.key)) return;
      const values = [];
      (state && state.selections[conf.key] ? state.selections[conf.key] : []).forEach((value) => {
        if (!values.some((v) => v.value === value)) values.push({ value, label: value, count: 0, selected: true });
      });
      if (!values.length) return;
      out.push({ key: conf.key, heading: conf.heading || conf.key, kind: 'list', values });
    });
    return out;
  }

  /* ---------- store stock: the store list stands in for "Ja" ---------- */

  /* Client card "Filter tilføjelse" (2026-10-09), on the search page too
     (Magnus 2026-10-15). The Spejder Sport app keeps custom.stock_locations on
     every product: "Online" (the warehouse) and each Klik & Hent store with the
     product in stock ("Østerport", "Lyngby" …). As on the collection pages
     (spejder-facet-chips, filters.liquid), those values replace the single
     "Ja" of "Kun på lager" (IsAvailable), under its heading and in its place,
     and the store facet gets no chip of its own. "Online" leads, then the
     stores in Search & Discovery's order (code points: Aalborg first,
     Østerport last; Danish collation reads "Aa" as "Å" and would put Aalborg
     after Østerport). With no store to show, "Ja" stays; without a Kun på
     lager facet, the store facet keeps its own heading.
     Per PRODUCT here, per variant on the collection pages: the Relewise app
     syncs no variant metafields, so on search "Sko/42" + "Østerport" can match
     a shoe whose 42 is at another store. */
  const STOCK_KEY = 'custom.stock_locations';
  const STOCK_HOST_KEY = 'IsAvailable';
  const ONLINE = 'Online';
  const byCodePoint = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

  function mergeStock(facets, facetsConfig) {
    const stock = facets.find((f) => f.kind === 'list' && f.key === STOCK_KEY);
    if (!stock) return facets;
    stock.values.sort((a, b) => {
      if (!!a.count !== !!b.count) return a.count ? -1 : 1;
      if ((a.value === ONLINE) !== (b.value === ONLINE)) return a.value === ONLINE ? -1 : 1;
      return byCodePoint(a.label, b.label);
    });
    const host = facets.find((f) => f.kind === 'list' && f.key === STOCK_HOST_KEY);
    const hostConf = (facetsConfig || []).find((f) => f.key === STOCK_HOST_KEY);
    if (!host && !hostConf) return facets;
    /* The stores toggle custom.stock_locations. A "Ja" picked before (an old
       filter.v.availability=1 link) stays listed, so it can still be undone. */
    const merged = {
      key: STOCK_HOST_KEY,
      heading: (host && host.heading) || hostConf.heading || stock.heading,
      kind: 'list',
      values: stock.values.map((v) => Object.assign({ key: STOCK_KEY }, v)).concat(host ? host.values.filter((v) => v.selected) : []),
    };
    const at = host || stock;
    return facets.filter((f) => f === at || (f !== stock && f !== host)).map((f) => (f === at ? merged : f));
  }

  /* ---------- rendering ---------- */

  const money = (n) => `${Math.round(n).toLocaleString('da-DK')} kr.`;
  const labelIn = (heading, list) => list.some((l) => l && l.toLowerCase() === (heading || '').toLowerCase());
  const settingList = (root, attr) => (root.getAttribute(attr) || '').split(',').map((s) => s.trim()).filter(Boolean);

  function tpl(root, name) {
    const t = root.querySelector(`template[data-rw-tpl="${name}"]`);
    return t ? t.content.firstElementChild.cloneNode(true) : null;
  }

  function fill(node, sel, text) {
    const el = sel ? node.querySelector(sel) : node;
    if (el) el.textContent = text;
    return el;
  }

  /** Style for a list facet: 'swatch' | 'pill' | 'checkbox' (heading-matched section settings). */
  function styleFor(root, facet) {
    if (labelIn(facet.heading, settingList(root, 'data-rw-swatch-labels'))) return 'swatch';
    if (labelIn(facet.heading, settingList(root, 'data-rw-pill-labels'))) return 'pill';
    return 'checkbox';
  }

  function swatchBg(root, label) {
    try {
      const map = JSON.parse(root.getAttribute('data-rw-swatch-map') || '{}');
      return map[(label || '').toLowerCase().trim()] || '';
    } catch (e) { return ''; }
  }

  function renderChips(root, facets, state, els) {
    els.chips.innerHTML = '';
    els.panels.innerHTML = '';
    facets.forEach((facet) => {
      const id = `spejder-facet-panel-${String(facet.key || facet.heading).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      const active = facet.kind === 'price' ? (state.price ? 1 : 0) : activeIn(facet);
      const chip = tpl(root, 'chip');
      if (!chip) return;
      const toggle = chip.querySelector('[data-facet-popout-toggle]');
      toggle.setAttribute('aria-controls', id);
      /* Defensive only: model() no longer emits a facet without values (it
         is left out instead) — should one arrive, keep it closed. */
      const empty = facet.kind !== 'price' && sectionsOf(facet).every((sec) => !sec.values.length);
      if (empty) { toggle.disabled = true; toggle.setAttribute('aria-disabled', 'true'); }
      const label = toggle.querySelector('[data-rw-chip-label]');
      if (label) label.textContent = facet.heading + (active ? ` (${active})` : '');
      els.chips.appendChild(chip);

      let panel;
      if (facet.kind === 'price') {
        panel = tpl(root, 'panel-price');
        const form = panel.querySelector('form');
        form.setAttribute('data-rw-action', 'price');
        const min = form.querySelector('[data-rw-price-min]');
        const max = form.querySelector('[data-rw-price-max]');
        min.placeholder = String(facet.range.min);
        max.placeholder = String(facet.range.max);
        if (facet.range.selMin != null) min.value = facet.range.selMin;
        if (facet.range.selMax != null) max.value = facet.range.selMax;
      } else {
        panel = tpl(root, 'panel-list');
        const list = panel.querySelector('[data-rw-list]');
        const style = styleFor(root, facet);
        const sections = sectionsOf(facet);
        sections.forEach((section, si) => {
          let target = list;
          if (si > 0) { target = document.createElement('ul'); target.className = 'spejder-facet-popout__list'; target.setAttribute('role', 'list'); }
          if (section.heading) {
            const h = document.createElement('p'); h.className = 'spejder-facet-popout__subheading'; h.textContent = section.heading;
            if (si === 0) panel.insertBefore(h, list); else panel.appendChild(h);
          }
          if (si > 0) panel.appendChild(target);
          section.values.forEach((v) => {
          const li = tpl(root, style === 'swatch' ? 'panel-option-swatch' : 'panel-option');
          const a = li.querySelector('a');
          const key = v.key || section.key;
          a.setAttribute('href', hrefFor(state, key, v));
          a.setAttribute('data-rw-action', 'toggle');
          a.setAttribute('data-rw-key', key);
          a.setAttribute('data-rw-value', v.value);
          a.classList.toggle('is-active', v.selected);
          if (v.selected) a.setAttribute('aria-current', 'true');
          if (v.count === 0 && !v.selected) a.classList.add('is-disabled');
          const sw = li.querySelector('.chip__swatch');
          if (sw) { const bg = swatchBg(root, v.label); if (bg) sw.style.background = bg; else sw.classList.add('chip__swatch--unmapped'); }
          fill(li, '.spejder-facet-popout__label', v.label);
          fill(li, '.spejder-facet-popout__count', String(v.count));
          target.appendChild(li);
          });
        });
      }
      panel.id = id;
      els.panels.appendChild(panel);
    });
  }

  /** href for a value toggle — a real URL so the link works without JS too. */
  function hrefFor(state, key, v) {
    const next = cloneState(state);
    toggleValue(next, key, v.value);
    const params = writeState(new URLSearchParams(window.location.search), next);
    params.delete('page');
    return `${window.location.pathname}?${params.toString()}`;
  }

  function cloneState(state) {
    const s = { selections: {}, price: state.price ? { min: state.price.min, max: state.price.max } : null };
    Object.keys(state.selections).forEach((k) => { s.selections[k] = state.selections[k].slice(); });
    return s;
  }

  function toggleValue(state, key, value) {
    const list = state.selections[key] || [];
    const i = list.indexOf(value);
    if (i === -1) list.push(value); else list.splice(i, 1);
    if (list.length) state.selections[key] = list; else delete state.selections[key];
    return state;
  }

  function renderActive(root, facets, state, els) {
    const pills = [];
    facets.forEach((facet) => {
      if (facet.kind === 'price') {
        if (state.price) pills.push({ label: `${money(facet.range.selMin != null ? facet.range.selMin : facet.range.min)} – ${money(facet.range.selMax != null ? facet.range.selMax : facet.range.max)}`, action: 'price-clear' });
        return;
      }
      sectionsOf(facet).forEach((sec) => sec.values.filter((v) => v.selected).forEach((v) => pills.push({ label: v.label, action: 'toggle', key: v.key || sec.key, value: v.value })));
    });
    /* Selections no facet represents — a legacy ?filter.p.product_type= link
       translated into a plain product data filter (relewise-listing.js). They
       really are filtering, so they need a pill; without one the shopper had a
       narrowed listing, no "Ryd alle" and no way back (2026-09-18). */
    const covered = {};
    facets.forEach((facet) => {
      if (facet.kind === 'price') return;
      sectionsOf(facet).forEach((sec) => sec.values.forEach((v) => { if (v.selected) covered[`${v.key || sec.key}|${v.value}`] = true; }));
    });
    Object.keys(state.selections).forEach((key) => {
      state.selections[key].forEach((value) => {
        if (!covered[`${key}|${value}`]) pills.push({ label: value, action: 'toggle', key, value });
      });
    });
    const targets = [els.active, els.footerActive].filter(Boolean);
    targets.forEach((host) => {
      host.innerHTML = '';
      host.hidden = pills.length === 0;
      if (!pills.length) return;
      const clear = tpl(root, host === els.active ? 'active-clear' : 'footer-clear');
      if (clear) { clear.setAttribute('href', clearHref()); clear.setAttribute('data-rw-action', 'clear'); host.appendChild(clear); }
      pills.forEach((p) => {
        const pill = tpl(root, host === els.active ? 'active-pill' : 'footer-pill');
        if (!pill) return;
        pill.setAttribute('data-rw-action', p.action);
        if (p.key) { pill.setAttribute('data-rw-key', p.key); pill.setAttribute('data-rw-value', p.value); }
        pill.setAttribute('href', p.action === 'toggle' ? hrefFor(state, p.key, { value: p.value }) : clearHref());
        fill(pill, '[data-rw-pill-label]', p.label);
        host.appendChild(pill);
      });
    });
  }

  function clearHref() {
    const params = writeState(new URLSearchParams(window.location.search), { selections: {}, price: null });
    params.delete('page');
    return `${window.location.pathname}?${params.toString()}`;
  }

  function renderDrawer(root, facets, state, els) {
    /* A fresh <collapsible-elements> each time: the theme's element binds its
       <details> children on connect. */
    const host = document.createElement('collapsible-elements');
    host.setAttribute('data-rw-groups', '');
    /* First render opens the section's default facets; every re-render keeps
       whatever the shopper has opened or closed since (a filter click must not
       collapse the accordion being used). */
    const rendered = els.groups.querySelectorAll('.filter-group').length > 0;
    const openLabels = rendered
      ? [].slice.call(els.groups.querySelectorAll('details[open]')).map((d) => (d.querySelector('[data-rw-group-label]') || {}).textContent || '')
      : settingList(root, 'data-rw-open-labels');
    const alphaLabels = settingList(root, 'data-rw-alpha-labels');
    facets.forEach((facet, i) => {
      const group = tpl(root, 'group');
      if (!group) return;
      const open = labelIn(facet.heading, openLabels);
      /* Defensive only (see renderChips): model() leaves valueless facets
         out, so the drawer lists exactly the facets this listing carries. */
      if (facet.kind !== 'price' && sectionsOf(facet).every((sec) => !sec.values.length)) group.setAttribute('data-facet-empty', '');
      const details = group.querySelector('details');
      const summary = group.querySelector('summary');
      const body = group.querySelector('[data-collapsible-body]');
      fill(group, '[data-rw-group-label]', facet.heading);
      if (open && !group.hasAttribute('data-facet-empty')) { details.setAttribute('open', 'true'); summary.setAttribute('aria-expanded', 'true'); body.style.height = 'auto'; }
      const content = group.querySelector('[data-collapsible-content]');
      if (facet.kind === 'price') {
        group.setAttribute('data-facet-style', 'checkbox');
        content.appendChild(renderPrice(root, facet, state));
      } else {
        const style = styleFor(root, facet);
        group.setAttribute('data-facet-style', style);
        if (facet.kind === 'cluster') {
          facet.sections.forEach((section) => {
            const h = document.createElement('h3'); h.className = 'filter-group__sublabel'; h.textContent = section.heading; content.appendChild(h);
            content.appendChild(renderValues(root, { key: section.key, heading: facet.heading, kind: 'list', values: section.values }, state, style, false));
          });
        } else {
          content.appendChild(renderValues(root, facet, state, style, labelIn(facet.heading, alphaLabels)));
        }
      }
      host.appendChild(group);
    });
    els.groups.replaceWith(host);
    els.groups = host;
  }

  function renderValues(root, facet, state, style, alpha) {
    const frag = document.createDocumentFragment();
    const ul = document.createElement('ul');
    ul.className = 'collection-nav' + (alpha ? ' collection-nav--alpha' : '');
    ul.setAttribute('data-facet-style', style);
    const cap = style === 'checkbox' ? 10 : 12;
    let prevLetter = '';
    facet.values.forEach((v, i) => {
      const hidden = i >= cap;
      if (alpha) {
        const letter = v.label.charAt(0).toUpperCase();
        if (letter !== prevLetter) {
          prevLetter = letter;
          const idx = document.createElement('li');
          idx.className = 'collection-nav__index' + (hidden ? ' hidden' : '');
          if (hidden) idx.setAttribute('data-link-hidden', '');
          idx.setAttribute('aria-hidden', 'true');
          idx.textContent = letter;
          ul.appendChild(idx);
        }
      }
      const li = tpl(root, style === 'swatch' ? 'value-swatch' : 'value');
      if (!li) return;
      li.classList.toggle('sidebar__item--active', v.selected);
      li.classList.toggle('link--remove', v.selected);
      li.classList.toggle('link--add', !v.selected && v.count > 0);
      li.classList.toggle('link--disable', !v.selected && v.count === 0);
      if (hidden) { li.classList.add('hidden'); li.setAttribute('data-link-hidden', ''); }
      const input = li.querySelector('input');
      const key = v.key || facet.key;
      const id = `rw-${key}-${v.value}`.replace(/[^a-zA-Z0-9_-]/g, '-');
      input.id = id;
      input.checked = v.selected;
      input.disabled = v.count === 0 && !v.selected;
      input.setAttribute('data-rw-action', 'toggle');
      input.setAttribute('data-rw-key', key);
      input.setAttribute('data-rw-value', v.value);
      const label = li.querySelector('label');
      label.setAttribute('for', id);
      const sw = li.querySelector('.chip__swatch');
      if (sw) { const bg = swatchBg(root, v.label); if (bg) sw.style.background = bg; else sw.classList.add('chip__swatch--unmapped'); }
      /* Pills carry no check box (filter-values-list.liquid renders the
         chip__check span for checkbox facets only). */
      if (style === 'pill') { const ck = li.querySelector('.chip__check'); if (ck) ck.remove(); }
      fill(li, '.chip__label', v.label);
      fill(li, '.chip__count', String(v.count));
      ul.appendChild(li);
    });
    frag.appendChild(ul);
    if (facet.values.length > cap) {
      const more = tpl(root, 'show-more');
      if (more) frag.appendChild(more);
    }
    return frag;
  }

  function renderPrice(root, facet, state) {
    const block = tpl(root, 'price');
    const r = facet.range;
    const selMin = r.selMin != null ? r.selMin : r.min;
    const selMax = r.selMax != null ? r.selMax : r.max;
    fill(block, '[data-rw-scale-min]', money(r.min));
    fill(block, '[data-rw-scale-max]', money(r.max));
    /* The theme's <range-slider> reads its bounds from these attributes on
       connect; a fresh element per render (cloned from the template) means
       it initialises against the current result's range. */
    const slider = block.querySelector('range-slider');
    slider.setAttribute('data-se-min', String(r.min));
    slider.setAttribute('data-se-max', String(r.max));
    slider.setAttribute('data-se-min-value', String(selMin));
    slider.setAttribute('data-se-max-value', String(selMax));
    const min = block.querySelector('[data-field-price-min]');
    const max = block.querySelector('[data-field-price-max]');
    min.placeholder = String(r.min); max.placeholder = String(r.max);
    min.min = String(r.min); min.max = String(r.max); max.min = String(r.min); max.max = String(r.max);
    if (r.selMin != null) min.value = r.selMin;
    if (r.selMax != null) max.value = r.selMax;
    min.setAttribute('data-rw-price-min', ''); max.setAttribute('data-rw-price-max', '');
    return block;
  }

  function renderCounts(state, hits, els) {
    const n = activeCount(state);
    document.querySelectorAll('[data-active-filters]').forEach((el) => { el.textContent = String(n); el.classList.toggle('hidden', n < 1); });
    if (els.cta) {
      const one = els.cta.getAttribute('data-rw-cta-one') || '';
      const many = els.cta.getAttribute('data-rw-cta-many') || '';
      const plain = els.cta.getAttribute('data-rw-cta-plain') || '';
      const label = els.cta.querySelector('[data-rw-cta-label]') || els.cta;
      label.textContent = n > 0 ? (hits === 1 ? one : many.replace('2000001', String(hits))) : plain;
    }
  }

  /** Full render. els: {chips, panels, active, groups, footerActive, cta}. */
  function render(root, result, facetsConfig, state, els) {
    const facets = clusterSizes(root, mergeStock(model(result, facetsConfig, state), facetsConfig));
    if (els.chips && els.panels) renderChips(root, facets, state, els);
    if (els.active || els.footerActive) renderActive(root, facets, state, els);
    if (els.groups) renderDrawer(root, facets, state, els);
    renderCounts(state, (result && result.hits) || 0, els);
    return facets;
  }

  /* ---------- sorting (from the app configuration) ---------- */

  /** [{id, label}] — the sort options the configuration exposes, in order. */
  function sortOptions(config, locale) {
    const mode = config && config.sortingMode;
    if (mode === 'hidden') return [];
    const list = config && Array.isArray(config.sorting) && config.sorting.length && mode !== 'default'
      ? config.sorting.filter((s) => s && s.enabled !== false)
      : [
        { id: 'Relevance', label: 'Relevans' }, { id: 'SalesPriceAsc', label: 'Pris: lav til høj' }, { id: 'SalesPriceDesc', label: 'Pris: høj til lav' },
        { id: 'AlphabeticallyAsc', label: 'Navn: A til Å' }, { id: 'AlphabeticallyDesc', label: 'Navn: Å til A' },
      ];
    return list.map((s) => ({
      id: s.id, label: (s.headingsByLocale && s.headingsByLocale[locale]) || s.label || s.id,
      type: s.type || 'builtin', key: s.key, requiresShopDomain: s.requiresShopDomain, order: s.order, selectionStrategy: s.selectionStrategy, mode: s.mode,
    }));
  }

  /** Applies the chosen sorting id to the builder (relevance = no explicit sorting). */
  function applySorting(builder, option) {
    /* Explicit relevance (= the personalized default) so the shopper's choice
       always wins over whatever the app's apply() set in custom sorting mode. */
    if (!option || option.id === 'Relevance') return builder.sorting((s) => s.sortByProductRelevance());
    const attr = (name, order) => builder.sorting((s) => s.sortByProductAttribute(name, order, (t) => t.sortByProductRelevance()));
    switch (option.id) {
      case 'SalesPriceAsc': return attr('SalesPrice', 'Ascending');
      case 'SalesPriceDesc': return attr('SalesPrice', 'Descending');
      case 'AlphabeticallyAsc': return attr('DisplayName', 'Ascending');
      case 'AlphabeticallyDesc': return attr('DisplayName', 'Descending');
      case 'BrandAsc': return attr('BrandName', 'Ascending');
      case 'BrandDesc': return attr('BrandName', 'Descending');
      case 'PopularityAsc': return builder.sorting((s) => s.sortByProductPopularity('Ascending', (t) => t.sortByProductRelevance()));
      case 'PopularityDesc': return builder.sorting((s) => s.sortByProductPopularity('Descending', (t) => t.sortByProductRelevance()));
      default:
        if (option.type === 'productData' && option.key) {
          const key = option.requiresShopDomain && shop ? `${shop}_${option.key}` : option.key;
          return builder.sorting((s) => s.sortByProductData(key, option.selectionStrategy || 'Product', option.order || 'Ascending', (t) => t.sortByProductRelevance(), option.mode || 'Auto'));
        }
    }
  }

  function renderSort(root, options, currentId, els) {
    if (!els.sortList) return;
    els.sortList.innerHTML = '';
    let current = null;
    options.forEach((o) => {
      const li = tpl(root, 'sort-option');
      if (!li) return;
      const a = li.querySelector('a');
      const active = o.id === currentId;
      li.classList.toggle('is-active', active);
      a.setAttribute('data-value', o.id);
      a.setAttribute('data-rw-action', 'sort');
      if (active) { a.setAttribute('aria-current', 'true'); current = o; }
      fill(a, 'span', o.label);
      els.sortList.appendChild(li);
    });
    if (els.sortLabel) els.sortLabel.textContent = (current || options[0] || {}).label || '';
    const popout = els.sortList.closest('.popout--sort');
    if (popout) popout.hidden = options.length === 0;
  }

  window.spejder = window.spejder || {};
  window.spejder.relewiseFacets = {
    readState, writeState, cloneState, toggleValue, activeCount,
    configuredFacets, applySelections, model, render,
    sortOptions, applySorting, renderSort,
  };
})();
